import { compare, deepClone } from 'fast-json-patch';
import emitter from 'tiny-emitter/instance';
import { EVENT } from '@/constants.js';
import { trigger, filterNonTriggered } from './triggers.js';
import requests from '../requests.js';
import Queue from '../../queue.js';
import boardTypes from './types/boards-types.js';
import { has } from '../../utils.js';

/**
 * Performs Updates on a project.
 */
class Updater {
  /**
   * Updater constructor.
   */
  constructor() {
    // Initialize an instance of updater only if there is none
    if (!Updater.instance) {
      this.initialize(null, null, null, null);
      Updater.instance = this;
    }
    return Updater.instance;
  }

  /**
   * Initialize the updater
   * @param {array}   hash      // The project that will receive the updates
   * @param {object}  state     // The current whole state
   * @param {number}  version   // The current project version
   * @param {object}  store     // The store instance
   */
  initialize(hash, state, version, store) {
    this.hash = hash;
    this.state = state;
    this.version = version;
    this.store = store;
    this.queue = new Queue();
    this.patch = [];
    this.actionStack = [];
    this.reverse = [];
    this.execIndex = 0;
    this.history = [];
    this.undoIndex = 0; // This index is just after the last operation (can be 0 if no operations left)
  }

  /**
   * Set the project version
   */
  setVersion(version) {
    this.version = version;
  }

  /**
   * A getter for the state property
   */
  getState() {
    return this.state;
  }

  /**
   * Return if there are unsaved patches in the queue
   * @returns {Boolean}   Whether there are unsent items
   */
  hasUnsent() {
    return !this.queue.isEmpty();
  }

  /**
   * Add the given patch operations to the patch
   * @param {array} patch
   */
  add(patch) {
    this.patch.push(...patch);
    // Also add any patch operations that are triggered
    const userId = this.store.getters['user/account/getId'];
    this.patch.push(...trigger(patch, userId));
  }

  addAction(action) {
    if (this.actionStack[this.actionStack.length - 1] !== action) {
      this.actionStack.push(action);
    }
  }

  /**
   * Execute the given patch operations. Applies the given patch to the state.
   * This is useful when we need to have an updated state before adding more patch operations.
   * @param   {array}       patch   The patch to execute
   * @returns {Object|null}         The reverse patch and the initial state or null on failure
   */
  execute(patch) {
    // Get a copy of the state to return it later
    const initial = deepClone(this.state);
    const startTime = performance.now();
    // const clonedState = deepClone(this.state);
    let patched = null;
    try {
      // We will execute the operations after the execIndex
      const clonedPatch = deepClone(patch.slice(this.execIndex));
      this.store.commit('project/updateProject', { patch: clonedPatch });
      patched = this.store.getters['project/getState'];
    } catch (error) {
      console.warn('Patch Error:', error);
      return { error: 'Patch Error', initial: null, reverse: null };
    }
    // Compare the state with the previous state to get the reverse patch
    const reverse = compare(patched, initial);

    const feature = this.store.getters['features/getFeature']('project_items');
    // Make sure there we do not exceed owner's limit
    if (feature && feature.limit > -1) {
      const itemCount = this.getItemCount(patched);
      const oldCount = this.getItemCount(initial);
      if (itemCount > feature.limit && itemCount > oldCount) {
        this.store.commit('project/updateProject', {
          patch: deepClone(reverse),
        });
        emitter.emit(EVENT.LIMIT_MODAL, {
          open: true,
          title:
            'You have already reached your 200 items limit for this workspace',
          subtitle: 'Upgrade to any of our plans to unlock unlimited items',
        });
        return { error: 'Limit error', initial: null, reverse: null };
      }
    }

    this.execIndex = patch.length;
    this.reverse.unshift(...reverse);

    return { error: null, reverse: this.reverse };
  }

  /**
   * Return the count of elements, jumpers and (non-container) components in a project
   * @param   {object}  state
   * @returns {number}          The items
   */
  getItemCount(state) {
    return (
      Object.keys(state.data.elements.byId).length +
      Object.keys(state.data.jumpers.byId).length +
      Object.keys(state.data.branches.byId).length +
      Object.values(state.data.components.byId).filter(c => !has(c, 'children'))
        .length +
      Object.values(state.data.comments.byId).filter(
        c => !has(c, 'root') && has(c, 'children')
      ).length
    );
  }

  /**
   * Complete the patch. Execute its operations, store it in history, reset patch, send it and adjust undo limit.
   * @param {Boolean} apply     Whether to actually complete the patch or do nothing
   * @param {Boolean} save      Whether to send the patch to the server
   * @param {Boolean} isUndo    Whether this is an undo operation
   * @param {Boolean} isRedo    Whether this is a redo operation
   * @param {Boolean} undoable  Whether to store it in history as an undoable operation
   * @param {Function} onSave   How to handle save response
   */
  async complete({
    apply,
    save = true,
    isUndo = false,
    isRedo = false,
    undoable = true,
    onSave = () => {},
  }) {
    console.log(
      'Called updater complete with parameter save:',
      save,
      ' apply: ',
      apply,
      ' patch.length: ',
      this.patch.length
    );
    if (!apply || (this.patch.length === 0 && !(isUndo || isRedo))) return;

    let fullPatch;
    let reverse;
    if (isUndo) {
      // If undo, execute the reverse of the previous index
      const { patch: undoPatch, reverse: undoReverse } =
        this.history[this.undoIndex - 1];
      fullPatch = deepClone(undoReverse);
      reverse = [];
    } else if (isRedo) {
      // If redo, execute the patch of the current index
      const { patch: undoPatch, reverse: undoReverse } =
        this.history[this.undoIndex];
      fullPatch = deepClone(undoPatch);
      reverse = [];
    } else {
      fullPatch = deepClone(this.patch);
      reverse = deepClone(this.reverse);
    }
    const actionStack = deepClone(this.actionStack);

    // Resume execution at the point that we left it
    const patch = fullPatch.slice(this.execIndex);
    this.reset();
    const { error, reverse: execReverse } = this.execute(patch);
    if (error) {
      console.error(error);
      return;
    }
    this.execIndex = 0;

    // Check if the state is invalid to apply additional patches only for regular patches (not undo/redo)
    let validatePatch = [];
    if (!(isUndo || isRedo)) {
      this.validate(this.state);
      validatePatch = deepClone(this.patch);
      // console.log('Validation patch is:', validatePatch);
      this.reset();
      const { error: validateError, reverse: validateReverse } =
        this.execute(validatePatch);
      this.execIndex = 0;

      if (!validateError) {
        patch.push(...validatePatch);
        execReverse.unshift(...validateReverse);
      }
    }

    reverse.push(...execReverse);

    if (undoable && !(isUndo || isRedo)) {
      console.log('Pushing patch and reverse in history:', patch, reverse);
      if (this.undoIndex < this.history.length) {
        this.history = this.history.splice(0, this.undoIndex);
      }
      this.history.push({ patch, reverse });
      this.undoIndex = this.history.length;
    }

    // Check if the user is at least a participant in the project
    const amIParticipant = this.store.getters['project/users/amIParticipant'];

    // If saving allowed
    if (
      save &&
      amIParticipant &&
      this.store.getters['project/getWorkspaceId'] ===
        this.store.getters['workspace/getId']
    ) {
      const sendPatch = fullPatch.concat(validatePatch);
      this.send(sendPatch, actionStack, onSave);
    }

    // Store the current undo index and limit
    if (isUndo) {
      this.reverse = [];
      this.undoIndex -= 1;
    } else if (isRedo) {
      this.reverse = [];
      this.undoIndex += 1;
    }
  }

  /**
   * Reset everything to prepare for the next patch execution.
   */
  reset() {
    this.patch = [];
    this.actionStack = [];
    this.reverse = [];
    this.execIndex = 0;
  }

  /**
   * State validation after patching
   * @param {object} state
   */
  validate(state) {
    this.validateBoards(state);
    this.validateComponents(state);
  }

  /**
   * Validate the board state after the patch
   */
  validateBoards(state) {
    let { open } = state.config.boards;
    if (!has(state.data.boards.byId, open)) {
      let found = null;

      // In case there is initial (previous) state, try to find some board near the old one
      if (state) {
        // Find the parent node
        let parent = Object.values(state.data.boards.byId).find(
          node => has(node, 'children') && node.children.includes(open)
        );

        while (parent) {
          // Find the index in the parent
          const index = parent.children.indexOf(open);
          // Find one node (non-container) after this index that is valid in current state
          found =
            parent.children
              .slice(index + 1)
              .filter(item => {
                const node = state.data.boards.byId[item];
                return node && !has(node, 'children');
              })
              .shift() || null;
          if (found) break;

          // Find one node (not-container) before this index that is valid in current state
          found =
            parent.children
              .slice(0, index)
              .reverse()
              .filter(item => {
                const node = state.data.boards.byId[item];
                return node && !has(node, 'children');
              })
              .shift() || null;
          if (found) break;

          open = parent;
          const p = parent;
          // Go to the next parent in initial state
          parent = Object.values(state.data.boards.byId).find(node => {
            return has(node, 'children') && node.children.includes(p.id);
          });
        }
      }
      // Get any node (non-container) from the root
      if (!found) {
        found =
          Object.values(state.data.boards.byId).find(item => {
            return !has(item, 'children') && item !== open;
          }) || null;
      }

      // Deselect everything in invalid open board
      this.store.dispatch('project/config/deselectAll', { apply: false });

      if (found) {
        // Open the board that was found
        this.store.dispatch('project/config/boards/setOpen', {
          id: found.id,
          apply: false,
        });
      } else {
        // Create a new board
        this.store.dispatch('project/data/boards/customCreate', {
          board: boardTypes.newItem,
          apply: false,
        });
      }
    }
  }

  /**
   * Validate the component state after the patch
   */
  validateComponents(state) {
    const { open } = state.config.components;
    if (open && !has(state.data.components.byId, open)) {
      this.store.dispatch('project/config/components/setOpen', {
        id: null,
        apply: false,
      });
    }
  }

  /**
   * Validate the starting state
   *
   */
  validateStartingState() {
    // Perform a state validation after the patch has been executed
    this.validate(this.state);
    const patch = deepClone(this.patch);
    const actionStack = deepClone(this.actionStack);
    if (patch.length === 0) return;
    this.reset();
    const { error, reverse } = this.execute(patch);
    this.execIndex = 0;
    if (error) return;
    // Check if the user is at least a participant in the project
    const amIParticipant = this.store.getters['project/users/amIParticipant'];

    // If saving allowed
    if (
      this.hash &&
      amIParticipant &&
      this.store.getters['project/getWorkspaceId'] ===
        this.store.getters['workspace/getId']
    ) {
      this.send(patch, actionStack);
    }
  }

  /**
   * Send the patch to the server
   */
  async send(patch, actionStack, onSave) {
    const index = this.history.length - 1;
    const fPatch = filterNonTriggered(patch);

    // Add the request to the queue
    console.log('Adding patch to the server queue:', fPatch);
    this.queue.add(
      requests.projects.patch,
      {
        hash: this.hash,
        value: fPatch,
        version: this.version,
        actionStack,
      },
      response => {
        this.version = response.data.version;
        if (onSave) onSave(response);
      },
      response => {
        const { status } = response;
        console.log('Status is:', status);
        if (status < 200 || status >= 300) {
          this.store.dispatch(
            'application/modals/setErrorModal',
            { value: status },
            { root: true }
          );
        }
      }
    );
  }

  /**
   * Returns the adjusted index taking into account all previous add-remove operations on the given path.
   * Operations that should lead to adjustment are operations before the given index.
   * @param   {String}  path
   * @returns {Number}
   */
  getIndexAdjustment(path) {
    // Split the path to the array and the index
    const index = path.split('/').pop();
    const prepath = path.slice(0, path.length - index.length);
    const num = parseInt(index, 10);

    // The adjusted index to be returned
    let adjustment = num;

    this.patch
      .filter(patchOp => patchOp.path.startsWith(prepath))
      .forEach(patchOp => {
        const opIndex = patchOp.path.split('/').pop();
        const opPrepath = patchOp.path.slice(
          0,
          patchOp.path.length - opIndex.length
        );
        const opNum = parseInt(opIndex, 10);
        if (opPrepath === prepath && opNum <= num) {
          if (patchOp.op === 'add') {
            adjustment += 1;
          } else if (patchOp.op === 'remove') {
            adjustment -= 1;
          }
        }
      });
    return adjustment;
  }

  /**
   * Returns if there are items in the undo list
   */
  canUndo() {
    return this.undoIndex > 0;
  }

  /**
   * Returns if there are items in the redo list
   */
  canRedo() {
    return this.undoIndex < this.history.length;
  }

  /**
   * Undo the last operation stored
   */
  undo() {
    console.log(
      'Undo called. Undo History: ',
      deepClone(this.history),
      'UndoIndex:',
      this.undoIndex
    );
    if (!this.canUndo()) return;
    this.complete({ apply: true, isUndo: true });
  }

  /**
   * Redoes the last patch in undo history by executing the operations.
   */
  redo() {
    if (!this.canRedo()) return;
    this.complete({ apply: true, isRedo: true });
  }
}

const updater = new Updater();
export default updater;
